import { useEffect } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import type { DbEndorsement } from '@domain/schemas/contract.schema';
import { ResourceState } from '@domain/schemas/contract.schema';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { endorsementQueries } from '@queries/fetchEndorsements';
import { useQuery } from '@tanstack/react-query';

import type { CreateRenewalFormState, MergeEndorsementsSelection } from '../types';
import type { MergeEndorsementsSectionProps } from '../views/MergeEndorsementsSection';

const getDefaultMergeSelection = (endorsements: DbEndorsement[]): MergeEndorsementsSelection => ({
  endorsementIds: endorsements.filter(({ status }) => status === ResourceState.FINAL).map(({ id }) => id),
  order: 'effective',
});

interface UseMergeEndorsementsParams {
  renewedContractId: string | undefined;
  value: MergeEndorsementsSelection | undefined;
  setValue: UseFormSetValue<CreateRenewalFormState>;
}

export const useMergeEndorsements = ({ renewedContractId, value, setValue }: UseMergeEndorsementsParams) => {
  const getFeatureFlag = useFeatureFlags();
  const isEndorsementsEnabled = getFeatureFlag('Endorsements');

  const {
    data: endorsements,
    isError,
    isLoading: isFetchingEndorsements,
  } = useQuery({
    ...endorsementQueries.forSubmission(renewedContractId as string),
    enabled: isEndorsementsEnabled && !!renewedContractId,
  });

  // Clear any stored selection when a different contract is picked — the handler rejects foreign endorsement ids
  useEffect(() => {
    setValue('mergeEndorsements', undefined);
  }, [renewedContractId, setValue]);

  const selection = value ?? getDefaultMergeSelection(endorsements ?? []);

  const sectionProps: MergeEndorsementsSectionProps | null =
    isEndorsementsEnabled && renewedContractId && endorsements
      ? {
          contractId: renewedContractId,
          endorsements,
          onChange: (next) => setValue('mergeEndorsements', next),
          value: selection,
        }
      : null;

  return {
    sectionProps,
    // A failed refetch keeps cached data and the checklist stays usable — only report the failure when there is nothing to render.
    hasEndorsementsError: isError && !endorsements,
    isFetchingEndorsements,
    mergePayload: selection.endorsementIds.length > 0 ? selection : undefined,
  };
};
